"use client";
import { useSpring, animated, useInView, useSprings } from '@react-spring/web';
import { useSeatsData } from '@/hooks/useSeatsData';
import { useEffect, useState } from 'react';
import { trackViewContent } from '../PixelTracker';
import WaitlistModal from '../ui/WaitlistModal';

export default function TestimonialsSection() {
  const { remainingSeats, totalSeats } = useSeatsData();
  const [isWaitlistModalOpen, setIsWaitlistModalOpen] = useState(false);
  const testimonials = [
    {
      name: "Abraham Dwek",
      role: "Facilitador Certificado · Julio 2024",
      quote: "Al atravesar problemas personales de salud y duelo, la certificación me acompañó a resignificar por completo mi saber respecto a los valores. Me reconstruí con nuevos conceptos y un plan de acción concreto.",
      highlight: "Transformación personal",
      transformation: "Resignificación completa",
      image: "/abraham.webp"
    },
    {
      name: "Trinidad Grieco",
      role: "Coach Certificada · Junio 2024",
      quote: "Es encontrarte con el Puzle de La Vida, pero con 'Brújula incorporada'. Los valores son la estrella polar indicando el norte… todo cobra un sentido diferente, todo encaja.",
      highlight: "Brújula para la vida",
      transformation: "Visión nueva y poderosa",
      image: "/trinidad.webp"
    },
    {
      name: "Fernando Preumayr",
      role: "Consultor Organizacional · Diciembre 2024",
      quote: "Trabajando sobre el propósito en las organizaciones lo pude asociar directamente a esta temática. Dar sentido a la vida personal y organizacional, lograr que ambos alineen es una de mis mayores aspiraciones.",
      highlight: "Alineación total",
      transformation: "Sentido profundo",
      image: "/fernando.webp"
    }
  ];

  const stats = [
    { number: "97%", label: "Aplican la metodología desde el día 1" },
    { number: "3x", label: "Incremento promedio en tarifas" },
    { number: "45", label: "Plazas limitadas para esta edición" },
    { number: "6", label: "Facilitadores en el mundo" }
  ];

  // Move all hooks to top level
  const [headerRef, headerInView] = useInView({ once: true, rootMargin: '-80px' });
  const [statsRef, statsInView] = useInView({ once: true, rootMargin: '-80px' });
  const [testimonialsRef, testimonialsInView] = useInView({ once: true, rootMargin: '-80px' });
  const [truthRef, truthInView] = useInView({ once: true, rootMargin: '-80px' });

  const headerSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: headerInView ? 1 : 0 },
    config: { duration: 400 }
  });

  const truthSpring = useSpring({
    opacity: truthInView ? 1 : 0,
    transform: truthInView ? 'scale(1)' : 'scale(0.95)',
    config: { duration: 400 }
  });

  // Stats animations
  const statsSprings = useSprings(
    stats.length,
    stats.map((_, index) => ({
      opacity: statsInView ? 1 : 0,
      transform: statsInView ? 'translateY(0px)' : 'translateY(20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Testimonials animations
  const testimonialsSprings = useSprings(
    testimonials.length,
    testimonials.map((_, index) => ({
      opacity: testimonialsInView ? 1 : 0,
      transform: testimonialsInView ? 'translateY(0px)' : 'translateY(20px)',
      config: { duration: 400 },
      delay: index * 50
    }))
  );

  // Track when testimonials section comes into view
  useEffect(() => {
    if (testimonialsInView) {
      trackViewContent('Sección Testimonios');
    }
  }, [testimonialsInView]);

  return (
    <section id="testimonials" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Powerful headline */}
        <animated.div
          ref={headerRef}
          className="text-center mb-16"
          style={headerSpring}
        >
          <p className="text-lg text-[#97d700] font-bold uppercase tracking-wider mb-4">
            Resultados que hablan solos
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-heading uppercase tracking-tight text-black mb-6">
            Ellos ya dieron el salto
            <span className="block">¿Y tú?</span>
          </h2>
        </animated.div>

        {/* Stats row */}
        <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
          {statsSprings.map((spring, index) => {
            const stat = stats[index];
            return (
              <animated.div
                key={index}
                className="text-center"
                style={spring}
              >
                <p className="text-4xl md:text-5xl font-heading text-[#97d700] mb-2">{stat.number}</p>
                <p className="text-sm text-gray-600">{stat.label}</p>
              </animated.div>
            );
          })}
        </div>

        {/* Testimonials with enhanced design */}
        <div ref={testimonialsRef} className="grid md:grid-cols-3 gap-8 mb-16">
          {testimonialsSprings.map((spring, index) => {
            const testimonial = testimonials[index];
            return (
              <animated.div
                key={index}
                className="relative bg-white p-8 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300"
                style={spring}
              >
                {/* Transformation badge */}
                <div className="absolute -top-4 left-8 bg-[#97d700] text-black px-4 py-2 rounded-full text-sm font-bold">
                  {testimonial.transformation}
                </div>

                {/* Quote */}
                <div className="mt-4">
                  <div className="text-6xl text-gray-200 font-serif leading-none">&ldquo;</div>
                  <p className="text-gray-700 italic mb-6 -mt-8 relative z-10">
                    {testimonial.quote}
                  </p>
                </div>

                {/* Highlight */}
                <div className="bg-gray-50 p-4 rounded-xl mb-6">
                  <p className="text-2xl font-heading text-[#97d700] text-center">
                    {testimonial.highlight}
                  </p>
                </div>

                {/* Person info */}
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full overflow-hidden shadow-md">
                    <img
                      src={testimonial.image}
                      alt={`Testimonio de ${testimonial.name}, ${testimonial.role} - Certificación Coaching X Valores`}
                      width={48}
                      height={48}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <p className="font-bold text-black">{testimonial.name}</p>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
              </animated.div>
            );
          })}
        </div>

        {/* The Truth Section */}
        <animated.div
          ref={truthRef}
          className="bg-black text-white p-8 md:p-16 rounded-3xl text-center"
          style={truthSpring}
        >
          <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-tight mb-8">
            La verdad incómoda
          </h3>
          <p className="text-xl md:text-2xl mb-8 text-gray-300 max-w-3xl mx-auto">
            Mientras lees esto, alguien más está tomando la decisión que podría cambiar su carrera para siempre.
          </p>
          <h3 className="text-3xl md:text-4xl font-heading uppercase tracking-tight text-white mb-6">
            {remainingSeats === 0 ? (
              <>Ya no queda <span className="text-[#97d700]">ninguna</span> plaza</>
            ) : (
              <>Solo queda <span className="text-[#97d700]">{remainingSeats}</span> plaza</>
            )}
          </h3>
          <p className="text-lg text-gray-400 mb-8">
            No es marketing. Es matemática simple:<br/>
            {totalSeats} plazas totales. Solo las primeras 30 en formato retiro con todo incluido. <br/>
            {totalSeats - remainingSeats} ya reservadas. {remainingSeats === 0 ? 'Edición completamente agotada.' : `${remainingSeats} oportunidades restantes.`}
          </p>
          {remainingSeats === 0 ? (
            <button
              onClick={() => setIsWaitlistModalOpen(true)}
              className="inline-block px-10 py-5 bg-white text-black font-heading uppercase tracking-wider font-bold text-xl rounded-full hover:bg-gray-100 transform hover:scale-105 transition-all duration-300"
              aria-label="Unirse a la lista de espera para próximas ediciones"
            >
              QUIERO PARTICIPAR EN PRÓXIMAS EDICIONES
            </button>
          ) : (
            <a
              href="#registration"
              className="inline-block px-10 py-5 bg-white text-black font-heading uppercase tracking-wider font-bold text-xl rounded-full hover:bg-gray-100 transform hover:scale-105 transition-all duration-300"
              aria-label="Solicitar una de las plazas restantes en la certificación"
            >
              APLICAR PARA LA ÚLTIMA PLAZA
            </a>
          )}
        </animated.div>
      </div>

      {/* Waitlist Modal */}
      <WaitlistModal
        isOpen={isWaitlistModalOpen}
        onClose={() => setIsWaitlistModalOpen(false)}
      />
    </section>
  );
}